import { useState } from "react";
import { Link } from "@tanstack/react-router";
import type { ProductStatusName } from "@shared/schemas/product";
import { EmptyState } from "@client/components/EmptyState";
import { SelectField, TextField } from "@client/components/Field";
import { ErrorNotice, LoadingBlock } from "@client/components/Notices";
import { ProductCard } from "@client/components/ProductCard";
import { useAuth } from "@client/hooks/useAuth";
import { useDebouncedValue } from "@client/hooks/useDebouncedValue";
import { useProducts } from "@client/hooks/useProducts";
import { buttonClass } from "@client/lib/buttonStyles";

const STATUS_OPTIONS: { value: "" | ProductStatusName; label: string }[] = [
  { value: "", label: "Any status" },
  { value: "ACTIVE", label: "Active" },
  { value: "DRAFT", label: "Draft" },
  { value: "ARCHIVED", label: "Archived" },
];

/**
 * The landing page: every product this person can see.
 *
 * "Can see" is decided by the server — this renders what comes back and never
 * filters for permission itself, so there is one answer to who sees what.
 */
export function ProductsPage() {
  const auth = useAuth();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"" | ProductStatusName>("");
  const [mine, setMine] = useState(false);

  const debouncedSearch = useDebouncedValue(search);
  const filters = {
    q: debouncedSearch.trim() || undefined,
    status: status || undefined,
    mine: mine || undefined,
  };
  const products = useProducts(filters);

  const items = products.data?.items ?? [];
  const filtered = Boolean(filters.q || filters.status || filters.mine);

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-xl font-bold text-ink">Products</h1>
          <p className="max-w-prose text-sm text-ink-soft">
            A product is the thing you are pricing: its name, its currency and its planning horizon.
            The numbers live in scenarios inside it, and anyone here can build their own.
          </p>
        </div>

        {auth.permissions.canCreateProduct && (
          <Link to="/products/new" className={buttonClass("primary")}>
            New product
          </Link>
        )}
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[14rem] flex-1">
          <TextField
            label="Search"
            type="search"
            placeholder="Name or description"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <div className="w-40">
          <SelectField
            label="Status"
            value={status}
            onChange={(event) => setStatus(event.target.value as "" | ProductStatusName)}
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </SelectField>
        </div>
        <label className="flex items-center gap-2 py-2 text-xs font-semibold text-ink">
          <input
            type="checkbox"
            checked={mine}
            onChange={(event) => setMine(event.target.checked)}
            className="h-4 w-4 accent-[var(--azure-deep)]"
          />
          Only products I own or belong to
        </label>
      </div>

      {products.isPending && <LoadingBlock rows={3} label="Loading products" />}

      {products.isError && (
        <ErrorNotice error={products.error} onRetry={() => void products.refetch()} />
      )}

      {products.isSuccess && items.length === 0 && filtered && (
        <EmptyState
          title="No products match those filters"
          description="Nothing here answers to that search, status and ownership combination."
          action={
            <button
              type="button"
              className={buttonClass("ghost", "sm")}
              onClick={() => {
                setSearch("");
                setStatus("");
                setMine(false);
              }}
            >
              Clear filters
            </button>
          }
        />
      )}

      {products.isSuccess && items.length === 0 && !filtered && (
        <EmptyState
          title="No products yet"
          description="A product owner creates the product and its baseline profile; everyone else forks that baseline into scenarios of their own."
          action={
            auth.permissions.canCreateProduct ? (
              <Link to="/products/new" className={buttonClass("primary", "sm")}>
                Create the first product
              </Link>
            ) : undefined
          }
          footnote={
            auth.permissions.canCreateProduct
              ? undefined
              : "Your role can build scenarios but not create products. Ask an administrator, or a product owner, to set one up."
          }
        />
      )}

      {items.length > 0 && (
        <>
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((product) => (
              <li key={product.id} className="flex">
                <ProductCard product={product} />
              </li>
            ))}
          </ul>
          {products.data && products.data.total > items.length && (
            <p className="text-xs text-ink-soft">
              Showing {items.length} of {products.data.total}. Narrow the search to see the rest.
            </p>
          )}
        </>
      )}
    </div>
  );
}
